import React, { useState } from "react";
import AsyncSelect from "react-select/async";
import { customFetch } from '../apiClient';
import DebitVoucherEditModal from "./DebitVoucherEditModal";

const customStyles = {
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
    backgroundColor: "white",
    color: "black",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isFocused ? "#ddd" : "white",
    color: "black",
  }),
};

const DebitVoucherSearch = () => {
  const [filters, setFilters] = useState({
    fromDate: new Date().toISOString().split("T")[0],
    toDate: new Date().toISOString().split("T")[0],
    vendorId: null,
    vendorName: ""
  });

  const [debitVouchers, setDebitVouchers] = useState([]);
  const [debitVoucherMeta, setDebitVoucherMeta] = useState({ count: 0, totalItems: 0 });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [expandedRows, setExpandedRows] = useState(new Set());

  // ✅ Edit Modal State
  const [showEditModal, setShowEditModal] = useState(false);
  const [editDebitVoucherData, setEditDebitVoucherData] = useState(null);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  };

  // Load vendor options for AsyncSelect
  const loadVendorOptions = async (inputValue) => {
    if (!inputValue || !inputValue.trim()) return [];
    
    try {
     const res = await customFetch(`/VendorDT/search?name=${encodeURIComponent(inputValue.trim())}`);
      
      if (!res.ok) return [];
      
      const data = await res.json();
      return data.map((v) => ({
        value: v.vid,
        label: `${v.vendorName} (${v.gstin || "No GST"})`
      }));
    } catch (err) {
      console.error("Load vendor options error:", err);
      return [];
    }
  };

  const handleVendorChange = (selected) => {
    setFilters((prev) => ({
      ...prev,
      vendorId: selected ? selected.value : null,
      vendorName: selected ? selected.label : ""
    }));
    setCurrentPage(1);
  };

  const handleSearch = async () => {
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      const params = new URLSearchParams();

      if (filters.fromDate) params.append("fromDate", filters.fromDate);
      if (filters.toDate) params.append("toDate", filters.toDate);
      if (filters.vendorName?.trim()) {
        params.append("vendorName", filters.vendorName.trim());
      }

      const res = await customFetch(`/DebitVoucher/search?${params.toString()}`);

      if (!res.ok) throw new Error(await res.text());

      const data = await res.json();
      console.log("Debit voucher search:", data);

      const list = Array.isArray(data.data) ? data.data : [];

      setDebitVouchers(list);
      setDebitVoucherMeta({
        count: data.count || list.length,
        totalItems: data.totalItems || 0
      });

      setSuccess(
        `Found ${data.count || list.length} debit voucher(s). Total Items: ${data.totalItems || 0}`
      );
      setCurrentPage(1);
      setExpandedRows(new Set());
    } catch (err) {
      setError(err.message);
      setDebitVouchers([]);
      setDebitVoucherMeta({ count: 0, totalItems: 0 });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFilters({
      fromDate: new Date().toISOString().split("T")[0],
      toDate: new Date().toISOString().split("T")[0],
      vendorId: null,
      vendorName: ""
    });
    setDebitVouchers([]);
    setDebitVoucherMeta({ count: 0, totalItems: 0 });
    setError("");
    setSuccess("");
    setCurrentPage(1);
    setExpandedRows(new Set());
  };

  // ✅ Edit handler
  const openEditModal = (debitVoucher) => {
    setEditDebitVoucherData(debitVoucher);
    setShowEditModal(true);
  };

  // Delete handler with reversal
  const handleDelete = async (debitVoucher) => {
    if (
      !window.confirm(
        `Delete Debit Voucher: ${debitVoucher.debitVchrID}?\n\nBill No: ${debitVoucher.summary?.billNo}\nVendor: ${debitVoucher.vendor?.vendorName}\nItems: ${debitVoucher.itemCount}\n\nThis will reverse DebitQTY for all items.`
      )
    )
      return;

    setError("");
    setSuccess("");

    try {
     const res = await customFetch(
        `/DebitVoucher/delete/${encodeURIComponent(debitVoucher.debitVchrID)}`,
        {
          method: "DELETE"
        }
      );

      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(errorText || "Failed to delete debit voucher");
      }

      const data = await res.json();
      console.log("Delete response:", data);

      setDebitVouchers((prev) => prev.filter((d) => d.debitVchrID !== debitVoucher.debitVchrID));

      setExpandedRows((prev) => {
        const newSet = new Set(prev);
        newSet.delete(debitVoucher.debitVchrID);
        return newSet;
      });

      setSuccess(
        `✅ ${data.message}: ${data.debitVchrId} (Bill: ${data.billNo}, ${data.itemsDeleted} items deleted)`
      );
    } catch (err) {
      setError(`❌ Delete failed: ${err.message}`);
      console.error("Delete error:", err);
    }
  };

  const toggleExpand = (debitVchrID) => {
    setExpandedRows((prev) => {
      const s = new Set(prev);
      if (s.has(debitVchrID)) s.delete(debitVchrID);
      else s.add(debitVchrID);
      return s;
    });
  };

  // Pagination
  const effectiveRows = rowsPerPage || 10;
  const totalRecords = debitVouchers.length;
  const totalPages = Math.ceil(totalRecords / effectiveRows);
  const startIndex = (currentPage - 1) * effectiveRows;
  const endIndex = startIndex + effectiveRows;
  const currentDebitVouchers = debitVouchers.slice(startIndex, endIndex);

  const handleRowsPerPageChange = (e) => {
    const rawValue = e.target.value;
    if (rawValue === "") {
      setRowsPerPage("");
      setCurrentPage(1);
      return;
    }
    let value = parseInt(rawValue);
    if (isNaN(value) || value < 1) value = 10;
    if (value > 100) value = 100;
    setRowsPerPage(value);
    setCurrentPage(1);
  };

  const handleRowsPerPageBlur = (e) => {
    if (e.target.value === "" || !rowsPerPage) {
      setRowsPerPage(10);
    }
  };

  const handlePageChange = (page) => setCurrentPage(page);

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    const pageNumbers = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    if (currentPage > 1) {
      pageNumbers.push(
        <button
          key="prev"
          className="btn btn-outline-primary btn-sm me-1"
          onClick={() => handlePageChange(currentPage - 1)}
        >
          Previous
        </button>
      );
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <button
          key={i}
          className={`btn btn-sm me-1 ${
            currentPage === i ? "btn-primary" : "btn-outline-primary"
          }`}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </button>
      );
    }

    if (currentPage < totalPages) {
      pageNumbers.push(
        <button
          key="next"
          className="btn btn-outline-primary btn-sm me-1"
          onClick={() => handlePageChange(currentPage + 1)}
        >
          Next
        </button>
      );
    }

    return pageNumbers;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const d = ("0" + date.getDate()).slice(-2);
    const m = ("0" + (date.getMonth() + 1)).slice(-2);
    const y = date.getFullYear();
    return `${d}-${m}-${y}`;
  };

  return (
    <div className="container-fluid mt-0fs-7">
      <div className="card shadow">
        <div className="card-header bg-danger text-white py-2">
          <h6 className="mb-0">🔙 Debit Voucher Search (Return/Damage)</h6>
        </div>
        <div className="card-body">
          {/* Filters */}
          <div className="row mb-3">
            <div className="col-md-2">
              <label className="form-label small">From Date</label>
              <input
                type="date"
                name="fromDate"
                value={filters.fromDate}
                onChange={handleFilterChange}
                className="form-control form-control-sm"
              />
            </div>

            <div className="col-md-2">
              <label className="form-label small">To Date</label>
              <input
                type="date"
                name="toDate"
                value={filters.toDate}
                onChange={handleFilterChange}
                className="form-control form-control-sm"
              />
            </div>

            <div className="col-md-3">
              <label className="form-label small">Vendor Name</label>
              <AsyncSelect
                cacheOptions
                loadOptions={loadVendorOptions}
                onChange={handleVendorChange}
                placeholder="Type vendor name..."
                isClearable
                styles={customStyles}
                value={
                  filters.vendorName
                    ? { value: filters.vendorId, label: filters.vendorName }
                    : null
                }
              />
            </div>

            <div className="col-md-2">
              <label className="form-label small">Rows per page</label>
              <input
                type="number"
                className="form-control form-control-sm"
                value={rowsPerPage}
                placeholder="10"
                min="1"
                max="100"
                onChange={handleRowsPerPageChange}
                onBlur={handleRowsPerPageBlur}
              />
            </div>

            <div className="col-md-3 mt-4">
              <button
                className="btn btn-primary btn-sm me-2"
                onClick={handleSearch}
                disabled={loading}
              >
                {loading ? "Searching..." : "Search"}
              </button>
              <button className="btn btn-secondary btn-sm" onClick={handleReset}>
                Reset
              </button>
            </div>
          </div>

          {error && <div className="alert alert-danger py-1">{error}</div>}
          {success && <div className="alert alert-success py-1">{success}</div>}

          {debitVouchers.length > 0 && (
            <div className="mb-2">
              <small className="text-muted">
                Showing {startIndex + 1} to {Math.min(endIndex, totalRecords)} of{" "}
                {totalRecords} record(s)
              </small>
            </div>
          )}

          {/* Results table */}
          <div className="table-responsive fs-9">
            <table className="table table-sm table-bordered table-hover">
              <thead className="table-dark">
                <tr>
                  <th style={{ width: "40px" }}></th>
                  <th>Sr</th>
                  <th>Debit Voucher ID</th>
                  <th>DVSRNO</th>
                  <th>Bill No</th>
                  <th>Bill Date</th>
                  <th>Vendor</th>
                  <th>Items</th>
                  <th>Total Amount</th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="10" className="text-center">
                      Loading...
                    </td>
                  </tr>
                ) : currentDebitVouchers.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="text-center">
                      No debit voucher records found.
                    </td>
                  </tr>
                ) : (
                  <>
                    {currentDebitVouchers.map((dv, index) => (
                      <React.Fragment key={dv.debitVchrID}>
                        <tr className="table-light">
                          <td className="text-center">
                            <i
                              className={`bi ${
                                expandedRows.has(dv.debitVchrID)
                                  ? "bi-chevron-down"
                                  : "bi-chevron-right"
                              }`}
                              style={{ cursor: "pointer" }}
                              onClick={() => toggleExpand(dv.debitVchrID)}
                            />
                          </td>
                          <td>{startIndex + index + 1}</td>
                          <td>
                            <strong>{dv.debitVchrID}</strong>
                          </td>
                          <td>{dv.dvsrno}</td>
                          <td>{dv.summary?.billNo || "N/A"}</td>
                          <td>{formatDate(dv.summary?.billDate)}</td>
                          <td>{dv.vendor?.vendorName || "N/A"}</td>
                          <td className="text-center">
                            <span className="badge bg-danger">
                              {dv.itemCount ?? dv.items?.length ?? 0}
                            </span>
                          </td>
                          <td className="text-end fw-bold text-danger">
                            ₹{Number(dv.summary?.totals || 0).toFixed(2)}
                          </td>
                          <td className="text-center">
                            {/* ✅ Edit Icon */}
                            <i
                              className="bi bi-pencil-square fs-7 me-2"
                              style={{ color: "#198754", cursor: "pointer" }}
                              title="Edit Debit Voucher"
                              onClick={() => openEditModal(dv)}
                            />
                            {/* Delete Icon */}
                            <i
                              className="bi bi-trash fs-7"
                              style={{ color: "#D71313", cursor: "pointer" }}
                              title="Delete Debit Voucher"
                              onClick={() => handleDelete(dv)}
                            />
                          </td>
                        </tr>

                        {/* Nested Item Rows */}
                        {expandedRows.has(dv.debitVchrID) &&
                          dv.items?.map((item, itemIdx) => (
                            <tr key={itemIdx} className="table-secondary">
                              <td></td>
                              <td></td>
                              <td colSpan="2" className="ps-4">
                                <small>└─ {item.medicine}</small>
                              </td>
                              <td>
                                <small>{item.groupName || "N/A"}</small>
                              </td>
                              <td>
                                <small>{item.companyName || "N/A"}</small>
                              </td>
                              <td>
                                <small>Batch: {item.batchNo || "N/A"}</small>
                              </td>
                              <td>
                                <small>
                                  <strong>Qty: {item.qty || 0} Pcs</strong>
                                </small>
                              </td>
                              <td>
                                <small>
                                  EXP: {item.expDate ? formatDate(item.expDate) : "N/A"}
                                </small>
                              </td>
                              <td className="text-end text-danger fw-bold">
                                <small>₹{Number(item.totals || 0).toFixed(2)}</small>
                              </td>
                            </tr>
                          ))}
                      </React.Fragment>
                    ))}
                  </>
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <div>
                <small className="text-muted">
                  Page {currentPage} of {totalPages}
                </small>
              </div>
              <div>{renderPagination()}</div>
            </div>
          )}
        </div>
      </div>

      {/* ✅ Edit Modal */}
      <DebitVoucherEditModal
        show={showEditModal}
        debitVoucherData={editDebitVoucherData}
        onClose={() => {
          setShowEditModal(false);
          setEditDebitVoucherData(null);
        }}
        onUpdate={handleSearch}
      />
    </div>
  );
};

export default DebitVoucherSearch;
